import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Company } from '@src/app/core/interfaces/common/company';
import { Option } from '@src/app/core/interfaces/common/option';
import { FormErrorHandlerService } from '@src/app/core/services/helpers/form-error-handler.service';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import {
  BenchmarkResponse,
  CompanyInvoicingParametersRequest,
  CompanyInvoicingParametersResponse,
  CreateBenchmarkRequest,
  FloatingInterestRateIndex,
  InvoicingHttpService,
  InvoicingParametersType,
  LoanOperationFilterRequest,
  LoanOperationWithBenchmarkResponse,
  UpdateBenchmarkRequest,
} from '@src/app/core/services/http/Invoicing/invoicing.service';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { LoanOperationTypesService } from '@src/app/features/dashboard/costs/debt-management/pages/modal-loan-details/loan-details/components/loan-operation-types.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { TableAction } from '@src/app/shared/components/table/table.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { catchError, first, forkJoin, of } from 'rxjs';

interface BenchmarkFormData {
  indexer: FloatingInterestRateIndex | null;
  percentage: number | null;
}

@Component({
  selector: 'app-company-invoicing-modal',
  standalone: true,
  templateUrl: './company-invoicing-modal.component.html',
  styleUrls: ['./company-invoicing-modal.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
})
export class CompanyInvoicingModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;
  @ViewChild('loanBenchmarkDialog') loanBenchmarkDialogRef: DialogComponent | null = null;

  @Input() company: Company | null = null;
  @Output() settingsUpdated = new EventEmitter<void>();

  // Tab management - Removed
  // activeTab: 'invoicing' | 'benchmark' = 'invoicing';

  // ========== INVOICING PARAMETERS ==========
  // Form properties
  selectedFeeType: string | null = null;
  flatFeePercentage: number = 2;
  successFeePercentage: number = 20;
  applyTaxes: boolean = false;
  taxPercentage: number = 19.53;
  applyDeskFee: boolean = false;
  deskFeePercentage: number = 0.2;

  // State management
  isLoadingInvoicing: boolean = false;
  isEditModeInvoicing: boolean = false;
  existingParameters: CompanyInvoicingParametersResponse | null = null;

  // ========== BENCHMARK ==========
  benchmarkForm!: FormGroup;
  benchmarkFormProps = nameof<BenchmarkFormData>();

  // Opções de indexador (apenas CDI e PRE)
  indexerOptions: Option[] = [
    { label: 'CDI', value: FloatingInterestRateIndex.CDI },
    { label: 'PRE', value: FloatingInterestRateIndex.PRE },
  ];

  // State management
  isLoadingBenchmark: boolean = false;
  isEditModeBenchmark: boolean = false;
  existingBenchmark: BenchmarkResponse | null = null;

  // ========== LOAN OPERATIONS ==========
  loanOperations: LoanOperationWithBenchmarkResponse[] = [];
  isLoadingLoans: boolean = false;
  loanColumns: ColumnDefinition[] = [];
  loanOperationsTotal: number = 0;
  loanOperationsCurrentPage: number = 1;
  loanOperationsPageSize: number = 10;
  loanOperationsSearchTerm: string = '';

  // Modal de benchmark de empréstimo
  selectedLoanOperation: LoanOperationWithBenchmarkResponse | null = null;
  loanBenchmarkForm!: FormGroup;
  isLoadingLoanBenchmark: boolean = false;
  isEditModeLoanBenchmark: boolean = false;
  existingLoanBenchmark: BenchmarkResponse | null = null;

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private invoicingService: InvoicingHttpService,
    private formErrorHandler: FormErrorHandlerService,
    private toastHelper: ToastHelperService,
    private loanOperationTypesService: LoanOperationTypesService,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
    this.buildBenchmarkForm();
    this.buildLoanBenchmarkForm();
    this.initializeLoanColumns();
  }

  private buildBenchmarkForm(): void {
    this.benchmarkForm = this.formBuilder.group({
      [this.benchmarkFormProps.indexer]: [null, [Validators.required]],
      [this.benchmarkFormProps.percentage]: [
        null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
    });
  }

  // Propriedade para controlar se o botão de salvar está desabilitado
  loanBenchmarkButtonDisabled: boolean = true;

  private buildLoanBenchmarkForm(): void {
    this.loanBenchmarkForm = this.formBuilder.group({
      ['loan_indexer']: [null, [Validators.required]],
      ['loan_percentage']: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
    });
  }

  // Adicione este novo método auxiliar
  private updateLoanBenchmarkButtonState(): void {
    const wasDisabled = this.loanBenchmarkButtonDisabled;
    this.loanBenchmarkButtonDisabled = !this.loanBenchmarkForm.valid;

    // Só força detecção se o estado mudou
    if (wasDisabled !== this.loanBenchmarkButtonDisabled) {
      this.cdr.detectChanges();
    }
  }

  private initializeLoanColumns(): void {
    this.loanColumns = [
      new ColumnDefinition({ field: 'sequentialId', header: 'Id' }),
      new ColumnDefinition({ field: 'contractId', header: 'Número do Contrato' }),
      new ColumnDefinition({
        field: 'relationshipAccount.bankRelationship.financialInstitution.institutionName',
        header: 'Banco',
      }),
      new ColumnDefinition({
        field: 'internationalCurrency.currencyCode',
        header: 'Moeda',
        template: 'currency',
      }),
      new ColumnDefinition({
        field: 'value',
        header: 'Valor Crédito (ME)',
        format: 'currency:2',
      }),
      new ColumnDefinition({
        field: 'operationDate',
        header: 'Data da Operação',
        format: 'date',
      }),
      new ColumnDefinition({
        field: 'loanOperationType',
        header: 'Tipo dívida',
        template: 'loanOperationType',
      }),
      new ColumnDefinition({
        field: 'status',
        header: 'Status',
        format: 'translateEnum',
      }),
      new ColumnDefinition({
        field: 'financialStatus',
        header: 'Status Financeiro',
        format: 'translateEnum',
      }),
      new ColumnDefinition({
        field: 'hasBenchmark',
        header: 'Possui Bench?',
        template: 'hasBenchmark',
      }),
      new ColumnDefinition({ field: 'benchmarkIndexer', header: 'Indexador' }),
      new ColumnDefinition({
        field: 'benchmarkPercentage',
        header: 'Percentual',
        format: 'percent:2',
      }),
    ];
  }

  async open(company: Company) {
    this.company = company;
    this.resetAllForms();

    // Abrir o modal primeiro
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'modal-company-invoicing', false, true);

    // Carregar todos os dados
    this.loadInvoicingParameters();
    this.loadBenchmark();
    this.loadLoanOperations();
  }

  // ========== TAB MANAGEMENT - Removed ==========
  // setActiveTab(tab: 'invoicing' | 'benchmark') {
  //   this.activeTab = tab;
  // }

  // ========== INVOICING PARAMETERS METHODS ==========
  private loadInvoicingParameters() {
    if (!this.company?.id) return;

    this.isLoadingInvoicing = true;
    this.invoicingService.getCompanyLoanInvoicingParameters(this.company.id).subscribe({
      next: parameters => {
        if (parameters) {
          this.isEditModeInvoicing = true;
          this.existingParameters = parameters;
          this.loadExistingParameters(parameters);
        } else {
          this.isEditModeInvoicing = false;
          this.existingParameters = null;
        }
        this.isLoadingInvoicing = false;
      },
      error: error => {
        console.error('Error loading invoicing parameters:', error);
        this.formErrorHandler.showToast(error, 'Erro ao carregar parâmetros de faturamento');
        this.isLoadingInvoicing = false;
      },
    });
  }

  private loadExistingParameters(parameters: CompanyInvoicingParametersResponse) {
    this.selectedFeeType = parameters.type;
    this.flatFeePercentage = parameters.type === 'FlatFee' ? parameters.percentage : 2;
    this.successFeePercentage = parameters.type === 'SuccessFee' ? parameters.percentage : 20;
    this.applyTaxes = parameters.applyTaxes;
    this.taxPercentage = parameters.taxPercentage || 19.53;
    this.applyDeskFee = parameters.applyDeskFee;
    this.deskFeePercentage = parameters.deskFeePercentage || 0.2;
  }

  onFeeTypeChange(feeType: string) {
    this.selectedFeeType = feeType;
  }

  onTaxToggle(enabled: boolean) {
    this.applyTaxes = enabled;
  }

  onDeskFeeToggle(enabled: boolean) {
    this.applyDeskFee = enabled;
  }

  isInvoicingFormValid(): boolean {
    if (!this.selectedFeeType || !this.company) {
      return false;
    }

    // Validate percentages based on selected fee type
    if (this.selectedFeeType === 'FlatFee') {
      if (this.flatFeePercentage <= 0 || this.flatFeePercentage > 100) {
        return false;
      }
    } else if (this.selectedFeeType === 'SuccessFee') {
      if (this.successFeePercentage <= 0 || this.successFeePercentage > 100) {
        return false;
      }
    }

    // Validate tax percentage if enabled
    if (this.applyTaxes && (this.taxPercentage <= 0 || this.taxPercentage > 100)) {
      return false;
    }

    // Validate desk fee percentage if enabled
    if (this.applyDeskFee && (this.deskFeePercentage <= 0 || this.deskFeePercentage > 100)) {
      return false;
    }

    return true;
  }

  submitInvoicingAction() {
    if (!this.isInvoicingFormValid() || !this.company) {
      this.toastHelper.showToastError('Preencha todos os campos obrigatórios');
      return;
    }

    this.isLoadingInvoicing = true;

    const payload: CompanyInvoicingParametersRequest = {
      companyId: this.company.id!,
      type: this.selectedFeeType as InvoicingParametersType,
      percentage:
        this.selectedFeeType === 'FlatFee' ? this.flatFeePercentage : this.successFeePercentage,
      applyTaxes: this.applyTaxes,
      taxPercentage: this.applyTaxes ? this.taxPercentage : undefined,
      applyDeskFee: this.applyDeskFee,
      deskFeePercentage: this.applyDeskFee ? this.deskFeePercentage : undefined,
    };

    const serviceCall = this.isEditModeInvoicing
      ? this.invoicingService.updateCompanyLoanInvoicingParameters(payload)
      : this.invoicingService.createCompanyLoanInvoicingParameters(payload);

    serviceCall.subscribe({
      next: () => {
        const message = this.isEditModeInvoicing
          ? 'Parâmetros de faturamento atualizados com sucesso'
          : 'Parâmetros de faturamento criados com sucesso';

        this.toastHelper.showToastSuccess(message);
        this.settingsUpdated.emit();
        this.isLoadingInvoicing = false;
      },
      error: error => {
        console.error('Error saving invoicing parameters:', error);
        const errorMessage = this.isEditModeInvoicing
          ? 'Erro ao atualizar parâmetros de faturamento'
          : 'Erro ao criar parâmetros de faturamento';

        this.formErrorHandler.showToast(error, errorMessage);
        this.isLoadingInvoicing = false;
      },
    });
  }

  // ========== BENCHMARK METHODS ==========
  private loadBenchmark() {
    if (!this.company?.id) return;

    this.isLoadingBenchmark = true;
    this.invoicingService.getBenchmarkByCompanyId(this.company.id).subscribe({
      next: benchmark => {
        if (benchmark) {
          this.isEditModeBenchmark = true;
          this.existingBenchmark = benchmark;
          this.loadExistingBenchmark(benchmark);
        } else {
          this.isEditModeBenchmark = false;
          this.existingBenchmark = null;
        }
        this.isLoadingBenchmark = false;
      },
      error: error => {
        console.error('Erro ao carregar benchmark:', error);
        this.formErrorHandler.showToastErrors(error);
        this.isLoadingBenchmark = false;
      },
    });
  }

  private loadExistingBenchmark(benchmark: BenchmarkResponse) {
    this.benchmarkForm.patchValue({
      [this.benchmarkFormProps.indexer]: benchmark.indexer,
      [this.benchmarkFormProps.percentage]: benchmark.percentage,
    });
  }

  isBenchmarkFormValid(): boolean {
    return this.benchmarkForm.valid;
  }

  submitBenchmarkAction() {
    if (!this.isBenchmarkFormValid() || !this.company?.id) {
      this.benchmarkForm.markAllAsTouched();
      return;
    }

    const formValue = this.benchmarkForm.value;
    const indexer = formValue[this.benchmarkFormProps.indexer] as FloatingInterestRateIndex;
    const percentage = formValue[this.benchmarkFormProps.percentage] as number;

    this.isLoadingBenchmark = true;

    if (this.isEditModeBenchmark && this.existingBenchmark) {
      // Atualizar benchmark existente
      const updateRequest: UpdateBenchmarkRequest = {
        id: this.existingBenchmark.id,
        indexer: indexer,
        percentage: percentage,
      };

      this.invoicingService
        .updateBenchmark(updateRequest)
        .pipe(
          first(),
          catchError(error => {
            this.formErrorHandler.showToastErrors(error);
            return of(null);
          })
        )
        .subscribe(result => {
          this.isLoadingBenchmark = false;
          if (result) {
            this.toastHelper.showToastSuccess('Benchmark atualizado com sucesso!');
            this.settingsUpdated.emit();
          }
        });
    } else {
      // Criar novo benchmark
      const createRequest: CreateBenchmarkRequest = {
        companyId: this.company.id,
        indexer: indexer,
        percentage: percentage,
      };

      this.invoicingService
        .createBenchmark(createRequest)
        .pipe(
          first(),
          catchError(error => {
            this.formErrorHandler.showToastErrors(error);
            return of(null);
          })
        )
        .subscribe(result => {
          this.isLoadingBenchmark = false;
          if (result) {
            this.toastHelper.showToastSuccess('Benchmark criado com sucesso!');
            this.settingsUpdated.emit();
          }
        });
    }
  }

  // ========== COMMON METHODS ==========
  private resetAllForms() {
    // Reset invoicing
    this.selectedFeeType = null;
    this.flatFeePercentage = 2;
    this.successFeePercentage = 20;
    this.applyTaxes = false;
    this.taxPercentage = 19.53;
    this.applyDeskFee = false;
    this.deskFeePercentage = 0.2;
    this.isEditModeInvoicing = false;
    this.existingParameters = null;

    // Reset benchmark
    this.benchmarkForm.reset();
    this.benchmarkForm.patchValue({
      [this.benchmarkFormProps.indexer]: null,
      [this.benchmarkFormProps.percentage]: null,
    });
    this.isEditModeBenchmark = false;
    this.existingBenchmark = null;

    // Reset loan operations pagination and search
    this.loanOperationsCurrentPage = 1;
    this.loanOperationsPageSize = 10;
    this.loanOperationsSearchTerm = '';
    this.loanOperationsTotal = 0;
  }

  isFormValid(): boolean {
    // Validação unificada
    const isInvoicingValid = this.isInvoicingFormValid();
    const isBenchmarkValid = this.isBenchmarkFormValid();

    // Ambos devem ser válidos para habilitar o botão
    return isInvoicingValid && isBenchmarkValid;
  }

  submitAction() {
    if (!this.isFormValid() || !this.company) {
      this.toastHelper.showToastError('Preencha todos os campos obrigatórios corretamente.');
      if (!this.isInvoicingFormValid()) {
        // Poderíamos adicionar lógica para destacar erro no invoicing
      }
      if (!this.isBenchmarkFormValid()) {
        this.benchmarkForm.markAllAsTouched();
      }
      return;
    }

    this.isLoading = true; // Usar isLoading genérico ou um específico para saveAll

    // Preparar observables
    const observables = [];

    // 1. Invoicing Parameters
    const invoicingPayload: CompanyInvoicingParametersRequest = {
      companyId: this.company.id!,
      type: this.selectedFeeType as InvoicingParametersType,
      percentage:
        this.selectedFeeType === 'FlatFee' ? this.flatFeePercentage : this.successFeePercentage,
      applyTaxes: this.applyTaxes,
      taxPercentage: this.applyTaxes ? this.taxPercentage : undefined,
      applyDeskFee: this.applyDeskFee,
      deskFeePercentage: this.applyDeskFee ? this.deskFeePercentage : undefined,
    };

    const invoicingRequest = this.isEditModeInvoicing
      ? this.invoicingService.updateCompanyLoanInvoicingParameters(invoicingPayload)
      : this.invoicingService.createCompanyLoanInvoicingParameters(invoicingPayload);

    observables.push(
      invoicingRequest.pipe(
        first(),
        catchError(error => {
          console.error('Erro ao salvar parâmetros:', error);
          this.formErrorHandler.showToast(error, 'Erro ao salvar parâmetros de faturamento');
          throw error; // Repassa erro para parar o forkJoin
        })
      )
    );

    // 2. Benchmark
    const benchmarkFormValue = this.benchmarkForm.value;
    const indexer = benchmarkFormValue[
      this.benchmarkFormProps.indexer
    ] as FloatingInterestRateIndex;
    const percentage = benchmarkFormValue[this.benchmarkFormProps.percentage] as number;

    if (this.isEditModeBenchmark && this.existingBenchmark) {
      const updateRequest: UpdateBenchmarkRequest = {
        id: this.existingBenchmark.id,
        indexer: indexer,
        percentage: percentage,
      };
      observables.push(
        this.invoicingService.updateBenchmark(updateRequest).pipe(
          first(),
          catchError(error => {
            console.error('Erro ao salvar benchmark:', error);
            this.formErrorHandler.showToastErrors(error);
            throw error;
          })
        )
      );
    } else {
      const createRequest: CreateBenchmarkRequest = {
        companyId: this.company.id!,
        indexer: indexer,
        percentage: percentage,
      };
      observables.push(
        this.invoicingService.createBenchmark(createRequest).pipe(
          first(),
          catchError(error => {
            console.error('Erro ao criar benchmark:', error);
            this.formErrorHandler.showToastErrors(error);
            throw error;
          })
        )
      );
    }

    // Executar todos
    forkJoin(observables).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastHelper.showToastSuccess('Configurações salvas com sucesso!');
        this.settingsUpdated.emit();
        this.dialogRef?.close();
      },
      error: () => {
        this.isLoading = false;
        // Erros já tratados nos catchError individuais
      },
    });
  }

  getCurrentLoading(): boolean {
    return this.isLoading || this.isLoadingInvoicing || this.isLoadingBenchmark;
  }

  getCurrentEditMode(): boolean {
    return this.isEditModeInvoicing || this.isEditModeBenchmark; // Apenas visual
  }

  closeModal(): void {
    this.dialogRef?.close();
  }

  // Helper methods for benchmark
  getSelectedIndexerLabel(): string {
    const selectedIndexer = this.benchmarkForm.get(this.benchmarkFormProps.indexer)?.value;
    if (!selectedIndexer) return '-';
    const option = this.indexerOptions.find(opt => opt.value === selectedIndexer);
    return option?.label || '-';
  }

  getPercentageValue(): number {
    return this.benchmarkForm.get(this.benchmarkFormProps.percentage)?.value || 0;
  }

  getLoanBenchmarkPercentageValue(): number {
    return this.loanBenchmarkForm.get('loan_percentage')?.value || 0;
  }

  getLoanBenchmarkIndexerLabel(): string {
    const selectedIndexer = this.loanBenchmarkForm.get('loan_indexer')?.value;
    if (!selectedIndexer) return '-';
    const option = this.indexerOptions.find(opt => opt.value === selectedIndexer);
    return option?.label || '-';
  }

  getTranslatedLoanOperationType(loanOperationType: string | null | undefined): string {
    return this.loanOperationTypesService.getTranslatedLabel(loanOperationType);
  }

  getFeeTypeLabel(type: string): string {
    switch (type) {
      case 'FlatFee':
        return 'Flat Fee';
      case 'SuccessFee':
        return 'Success Fee';
      default:
        return 'Outro';
    }
  }

  // ========== LOAN OPERATIONS METHODS ==========
  private loadLoanOperations() {
    if (!this.company?.id) return;

    this.isLoadingLoans = true;

    // Construir filtro com paginação e pesquisa
    const filter: LoanOperationFilterRequest = {
      companyId: this.company.id,
      pageNumber: this.loanOperationsCurrentPage,
      pageSize: this.loanOperationsPageSize,
    };

    // Adicionar termo de pesquisa se houver
    if (this.loanOperationsSearchTerm && this.loanOperationsSearchTerm.trim()) {
      filter.filter = this.loanOperationsSearchTerm.trim();
    }

    // Usar o novo endpoint otimizado que retorna empréstimos já com benchmark incluído
    this.invoicingService
      .getLoanOperationsWithBenchmark(filter)
      .pipe(
        first(),
        catchError(error => {
          console.error('Error loading loan operations:', error);
          this.formErrorHandler.showToast(error, 'Erro ao carregar empréstimos');
          return of({ data: [], total: 0, currentPage: 1, pages: 0, itemsPerPage: 0 });
        })
      )
      .subscribe(response => {
        this.loanOperations = response.data;
        this.loanOperationsTotal = response.total;
        this.loanOperationsCurrentPage = response.currentPage;
        this.isLoadingLoans = false;
      });
  }

  handleLoanTablePagination({ page = 0, rows = 0 }: PaginatorState): void {
    this.loanOperationsCurrentPage = page + 1; // PrimeNG usa índice 0, API usa índice 1
    this.loanOperationsPageSize = rows;
    this.loadLoanOperations();
  }

  handleLoanTableSearch(searchTerm: string): void {
    this.loanOperationsSearchTerm = searchTerm;
    this.loanOperationsCurrentPage = 1; // Resetar para primeira página ao pesquisar
    this.loadLoanOperations();
  }

  getMenuActions(loan: LoanOperationWithBenchmarkResponse): MenuItem[] {
    return [
      {
        label: loan.hasBenchmark ? 'Editar Benchmark' : 'Cadastrar Benchmark',
        icon: 'pi pi-chart-line',
        action: 'edit-benchmark',
        command: () => this.openLoanBenchmarkModal(loan),
      },
    ];
  }

  handleLoanTableAction(action: TableAction<LoanOperationWithBenchmarkResponse>) {
    switch (action.action) {
      case 'edit-benchmark':
        this.openLoanBenchmarkModal(action.item);
        break;
      default:
        break;
    }
  }

  openLoanBenchmarkModal(loan: LoanOperationWithBenchmarkResponse) {
    this.selectedLoanOperation = loan;
    this.isEditModeLoanBenchmark = loan.hasBenchmark;
    this.isLoadingLoanBenchmark = true;

    if (loan.hasBenchmark) {
      // Preencher formulário com valores já disponíveis
      if (loan.benchmarkIndexer && loan.benchmarkPercentage !== null) {
        this.loanBenchmarkForm.patchValue({
          loan_indexer: loan.benchmarkIndexer,
          loan_percentage: loan.benchmarkPercentage,
        });
      }

      // Carregar benchmark existente para obter o ID completo
      this.invoicingService
        .getBenchmarkForLoanOperation(loan.id)
        .pipe(
          first(),
          catchError(error => {
            console.error('Error loading loan benchmark:', error);
            return of(null);
          })
        )
        .subscribe(benchmark => {
          this.existingLoanBenchmark = benchmark;
          if (benchmark) {
            this.loanBenchmarkForm.patchValue({
              loan_indexer: benchmark.indexer,
              loan_percentage: benchmark.percentage,
            });
          }
          this.isLoadingLoanBenchmark = false;
          this.cdr.detectChanges(); // Mudança: usar detectChanges ao invés de markForCheck
          this.openLoanBenchmarkDialog();
        });
    } else {
      // Novo benchmark - resetar formulário
      this.existingLoanBenchmark = null;
      this.loanBenchmarkForm.reset({
        loan_indexer: null,
        loan_percentage: null,
      });
      this.isLoadingLoanBenchmark = false;
      this.cdr.detectChanges(); // Mudança: usar detectChanges ao invés de markForCheck
      this.openLoanBenchmarkDialog();
    }
  }
  private openLoanBenchmarkDialog() {
    this.loanBenchmarkDialogRef?.open(
      '',
      '',
      '',
      'Cancelar',
      '',
      'modal-loan-benchmark',
      false,
      true
    );

    // Força detecção após abrir o modal
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  public submitLoanBenchmarkAction(): void {
    if (!this.loanBenchmarkForm.valid || !this.selectedLoanOperation) {
      this.loanBenchmarkForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const formValue = this.loanBenchmarkForm.value;
    const indexer = formValue['loan_indexer'] as FloatingInterestRateIndex;
    const percentage = formValue['loan_percentage'] as number;

    this.isLoadingLoanBenchmark = true;

    if (this.isEditModeLoanBenchmark && this.existingLoanBenchmark) {
      // Atualizar benchmark existente
      const updateRequest: UpdateBenchmarkRequest = {
        id: this.existingLoanBenchmark.id,
        indexer: indexer,
        percentage: percentage,
      };

      this.invoicingService
        .updateBenchmark(updateRequest)
        .pipe(
          first(),
          catchError(error => {
            this.formErrorHandler.showToastErrors(error);
            this.isLoadingLoanBenchmark = false;
            return of(null);
          })
        )
        .subscribe(result => {
          this.isLoadingLoanBenchmark = false;
          if (result) {
            this.toastHelper.showToastSuccess('Benchmark do empréstimo atualizado com sucesso!');
            this.loadLoanOperations(); // Recarregar lista
            this.closeLoanBenchmarkModal();
          }
        });
    } else {
      // Criar novo benchmark
      const createRequest: CreateBenchmarkRequest = {
        operationId: this.selectedLoanOperation.id,
        indexer: indexer,
        percentage: percentage,
      };

      this.invoicingService
        .createBenchmark(createRequest)
        .pipe(
          first(),
          catchError(error => {
            this.formErrorHandler.showToastErrors(error);
            this.isLoadingLoanBenchmark = false;
            return of(null);
          })
        )
        .subscribe(result => {
          this.isLoadingLoanBenchmark = false;
          if (result) {
            this.toastHelper.showToastSuccess('Benchmark do empréstimo criado com sucesso!');
            this.loadLoanOperations(); // Recarregar lista
            this.closeLoanBenchmarkModal();
          }
        });
    }
  }

  public closeLoanBenchmarkModal(): void {
    this.loanBenchmarkDialogRef?.close();
    this.selectedLoanOperation = null;
    this.loanBenchmarkForm.reset();
    this.isEditModeLoanBenchmark = false;
    this.existingLoanBenchmark = null;
    this.loanBenchmarkButtonDisabled = true;
  }

  public isLoanBenchmarkFormValid(): boolean {
    return this.loanBenchmarkForm.valid;
  }
}
